<script setup lang="ts">
import { onBeforeUnmount, watch } from "vue";
import AppIcon from "./AppIcon.vue";

/**
 * El diálogo que comparten las pantallas: la credencial, las filas del registro,
 * las confirmaciones.
 *
 * Se pinta distinto según el aparato, y no por gusto:
 *
 * • **En computadora**, tarjeta centrada. Es lo que uno espera de un diálogo con
 *   un ratón enfrente, y el contenido queda a la altura de los ojos.
 *
 * • **En teléfono**, hoja pegada abajo. Tres razones, y las tres se sienten:
 *   los botones quedan donde alcanza el pulgar; al abrirse el teclado la hoja se
 *   comprime desde arriba en vez de saltar; y es el gesto que la gente ya trae
 *   aprendido de cualquier app del teléfono.
 *
 * La altura va en `dvh` y no en `vh`: en Safari de iPhone, `vh` mide la pantalla
 * **con la barra de direcciones escondida**, así que un `70vh` honesto se
 * convierte en una hoja que se sale por abajo en cuanto la barra aparece. `dvh`
 * mide lo que de veras se ve en este momento.
 */
const props = defineProps<{ open: boolean; title: string; subtitle?: string; wide?: boolean }>();
const emit = defineEmits<{ close: [] }>();

function onKeydown(event: KeyboardEvent): void {
  if (event.key === "Escape") emit("close");
}

watch(
  () => props.open,
  (open) => {
    if (open) {
      window.addEventListener("keydown", onKeydown);
      document.body.style.overflow = "hidden";
    } else {
      window.removeEventListener("keydown", onKeydown);
      document.body.style.overflow = "";
    }
  },
);

onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeydown);
  document.body.style.overflow = "";
});
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="open"
        class="fixed inset-0 z-50 flex items-end justify-center bg-black/25 backdrop-blur-[3px] sm:items-center sm:overflow-y-auto sm:p-4"
        role="dialog"
        aria-modal="true"
        @click.self="emit('close')"
      >
        <div
          class="modal-panel flex max-h-[92dvh] w-full flex-col rounded-t-[20px] border border-separator bg-canvas-elevated shadow-float sm:max-h-[90dvh] sm:rounded-[18px]"
          :class="wide ? 'sm:max-w-3xl' : 'sm:max-w-lg'"
        >
          <!-- El tirador: la señal de «esto se arrastra/se cierra», solo en teléfono. -->
          <div class="flex shrink-0 justify-center pt-2 pb-0.5 sm:hidden">
            <span class="h-1 w-9 rounded-full bg-separator-strong" />
          </div>

          <header class="flex shrink-0 items-start justify-between gap-4 px-5 pt-3 pb-3 sm:pt-4">
            <div class="min-w-0">
              <h2 class="text-[17px] font-semibold tracking-[-0.01em] text-content sm:text-[16px]">
                {{ title }}
              </h2>
              <p v-if="subtitle" class="mt-0.5 text-[13px] text-secondary">{{ subtitle }}</p>
            </div>
            <!--
              44px de blanco en táctil: una X de 28px se falla con el pulgar, y
              fallarle a la X de un diálogo es picarle a lo que hay detrás.
            -->
            <button
              class="toque -mt-1.5 -mr-2 grid size-9 shrink-0 place-items-center rounded-full text-tertiary transition hover:bg-surface-hover hover:text-content sm:-mt-0.5 sm:-mr-1 sm:size-7"
              aria-label="Cerrar"
              @click="emit('close')"
            >
              <AppIcon name="close" :size="16" />
            </button>
          </header>

          <!--
            `min-h-0` es lo que deja que este bloque encoja dentro del flex y sea
            él quien scrollee. Sin eso, el contenido empuja la hoja y el pie —los
            botones de guardar— se sale de la pantalla.
          -->
          <div class="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-1">
            <slot />
          </div>

          <footer
            v-if="$slots.footer"
            class="pb-safe-plus mt-2 flex shrink-0 items-center justify-end gap-2 border-t border-separator px-5 pt-3.5"
          >
            <slot name="footer" />
          </footer>
          <!-- Sin pie, el respiro de abajo lo pone el propio contenedor. -->
          <div v-else class="pb-safe shrink-0" />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
